<template>
  <div class="formulator__sliders">
    <formulator-slider
      :key="item ? item.value : 'no-item'"
      :name="name"
      :unit="unit"
      :min="effectiveMin"
      :max="effectiveMax"
      :interval="interval"
      v-model="amount"
      :info-modal="infoModal"
      :disabled="disableSlider"
      :class="{'formulator__sliders--disabled': disableSlider }"
    >
      <template #title>
        <slot name="title" />
      </template>
    </formulator-slider>
    <div class="select-group">
      <p
        v-if="errorMessage"
        style="color: red; font-size: small; position: absolute; margin-top: -1px;"
      >
        No se puede repetir los mismos aditivos.
      </p>
      <p class="select-group__title">
&nbsp;
      </p>
      <ev-select
        v-model="item"
        :options="items"
      />
    </div>
  </div>
</template>
<script>
import evSelect from '../ev-select.vue';
import formulatorSlider from './formulator-slider-additive.vue';
import sliderValues from './slider-values';

function normalizeAdditiveAmount(rawAmount, {
  item = null,
  fallbackMax = 0,
  fallbackMin = 0,
} = {}) {
  let amount = Number(rawAmount);
  if (!Number.isFinite(amount) || amount < 0) amount = 0;
  amount = sliderValues.round(amount);

  const configuredMax = item && item.maxPercentage != null
    ? Number(item.maxPercentage)
    : Number(fallbackMax);
  const max = Math.max(configuredMax, amount);
  const min = item && item.minPercentage != null
    ? Number(item.minPercentage)
    : Number(fallbackMin);

  if (amount > max) amount = max;
  if (amount < min) amount = min;

  return amount;
}

export default {
  components: {
    evSelect,
    formulatorSlider,
  },
  data() {
    const valueId = this.value && this.value.id;
    const item = this.items.find(entry => String(entry.value) === String(valueId)) || null;
    return {
      amount: normalizeAdditiveAmount(this.value && this.value.amount, {
        item,
        fallbackMax: this.max,
        fallbackMin: this.min,
      }),
      item,
      syncingFromValue: false,
    };
  },
  props: {
    min: {
      type: [Number, String],
      required: true,
    },
    max: {
      type: [Number, String],
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    errorMessage: {
      type: Boolean,
      default: false,
      required: false,
    },
    unit: {
      type: String,
      required: true,
    },
    interval: {
      type: [Number, String],
      required: true,
    },
    infoModal: {
      type: String,
      default: null,
    },
    value: {
      type: Object,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    initial: {
      type: Number,
      default: 0,
    },
    updateOnSelect: {
      type: Boolean,
      default: true,
    },
    // Al cambiar de ítem mantiene el porcentaje elegido (acotado al máximo del
    // nuevo ítem) en vez de volver a 0 o al valor por defecto.
    keepAmountOnSelect: {
      type: Boolean,
      default: false,
    },
    disableSlider: {
      type: Boolean,
      default: false,
    },
    showCustomAmount: {
      type: Boolean,
      default: false,
    },
    customAmount: {
      type: Number,
      default: 0,
    },
    isIngredient: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    itemValue() {
      const valueObj = {
        amount: this.showCustomAmount ? this.customAmount : this.amount,
        id: this.item ? this.item.value : null,
      };
      if (this.isIngredient) {
        valueObj.ms = this.item ? this.item.ms : 0;
      }

      return valueObj;
    },
    configuredMax() {
      if (this.item && this.item.maxPercentage != null) {
        return Number(this.item.maxPercentage);
      }
      return Number(this.max);
    },
    effectiveMax() {
      return Math.max(this.configuredMax, Number(this.amount) || 0);
    },
    effectiveMin() {
      if (this.item && this.item.minPercentage != null) {
        return Number(this.item.minPercentage);
      }
      return Number(this.min);
    },
    range() {
      return this.effectiveMax - this.effectiveMin;
    },
  },
  methods: {
    findItem(id) {
      if (id == null) return null;
      return this.items.find(item => String(item.value) === String(id)) || null;
    },
    normalizeAmount(rawAmount, item = this.item) {
      return normalizeAdditiveAmount(rawAmount, {
        item,
        fallbackMax: this.max,
        fallbackMin: this.min,
      });
    },
    syncFromValue(val) {
      if (!val) return;
      this.syncingFromValue = true;
      this.item = this.findItem(val.id);
      this.amount = this.normalizeAmount(val.amount, this.item);
      this.$nextTick(() => {
        this.syncingFromValue = false;
      });
    },
    updateDefaultValue() {
      this.$store.dispatch('setUpdated', false);
      if (this.item && this.updateOnSelect) {
        // eslint-disable-next-line no-magic-numbers
        this.amount = this.normalizeAmount(
          (this.range * Number(this.item.defaultPercentage) / 100) + this.effectiveMin,
        );
      }
    },
    clampAmount() {
      this.amount = this.normalizeAmount(this.amount);
    },
    // effectiveMax deja pasar montos sobre el máximo (fórmula hidratada), así
    // que al cambiar de ítem hay que acotar explícitamente al nuevo máximo.
    keepAmountWithinMax() {
      this.$store.dispatch('setUpdated', false);
      this.amount = this.normalizeAmount(Math.min(Number(this.amount) || 0, this.configuredMax));
    },
    setInitial() {
      if (!this.item && this.items.length > this.initial) this.item = this.items[this.initial];
    },
  },
  mounted() {
    this.setInitial();
    this.amount = this.normalizeAmount(this.amount);
  },
  watch: {
    value: {
      deep: true,
      handler(val) {
        const currentId = this.item ? this.item.value : null;
        if (String(currentId) === String(val && val.id)
          && Number(this.amount) === Number(val && val.amount)) {
          return;
        }
        this.syncFromValue(val);
      },
    },
    item(newItem, oldItem) {
      if (this.syncingFromValue) return;
      if (this.keepAmountOnSelect && oldItem) {
        this.keepAmountWithinMax();

        return;
      }
      this.updateDefaultValue();
      this.clampAmount();
    },
    itemValue(val) {
      this.$emit('input', val);
    },
    items() {
      if (!this.item && this.value && this.value.id != null) {
        this.item = this.findItem(this.value.id);
        this.amount = this.normalizeAmount(this.value.amount, this.item);
        return;
      }
      this.setInitial();
    },
    showCustomAmount(val) {
      if (val) this.amount = this.customAmount;
    },
  },
};
</script>
